import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { shiftService } from '@/api/domains/shift';
import { SHIFT_APPROVAL_OUTCOME, SHIFT_REQUEST_DECISION } from '@/api/domains/shift/shift.types';
import type {
  CompanyShiftRequest,
  LC_ShiftRequestResolvedState,
  ShiftRequestStatus,
} from '@/api/domains/shift/shift.types';
import { useUI } from '@/hooks/ui/useUI';
import { formatToDdMmYyyy } from '@/utils';


const STATUS_I18N_KEYS: Record<ShiftRequestStatus, string> = {
  10: 'attendance:shiftChangeAdmin.status.pending',
  20: 'attendance:shiftChangeAdmin.status.approved',
  30: 'attendance:shiftChangeAdmin.status.rejected',
  40: 'attendance:shiftChangeAdmin.status.canceled',
};

interface UseShiftChangeRequestResolutionParams {
  request: CompanyShiftRequest;
  onApproved?: () => void;
  onRejected?: () => void;
  onConflict?: () => void;
}

export interface UseShiftChangeRequestResolutionResult {
  displayRequest: CompanyShiftRequest;
  comment: string;
  setComment: (value: string) => void;
  canSubmit: boolean;
  isSubmitting: boolean;
  handleApprove: () => Promise<void>;
  handleReject: () => Promise<void>;
}

/**
 * Lógica compartida para aprobar o rechazar una solicitud de cambio de turno.
 * Unifica useShiftChangeRequestApproval y useShiftChangeRequestRejection en un único hook
 * que delega al endpoint unificado resolve con el discriminador de decisión apropiado.
 */
export function useShiftChangeRequestResolution({
  request,
  onApproved,
  onRejected,
  onConflict,
}: UseShiftChangeRequestResolutionParams): UseShiftChangeRequestResolutionResult {
  const { t: translate } = useTranslation();
  const { showApiError, showSuccess, showWarning } = useUI();

  const [displayRequest, setDisplayRequest] = useState<CompanyShiftRequest>(request);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit = comment.trim().length > 0 && !isSubmitting;

  const applyResolvedState = useCallback((current: LC_ShiftRequestResolvedState) => {
    setDisplayRequest((previous) => ({
      ...previous,
      status:
        current.statusDescription ||
        translate(STATUS_I18N_KEYS[current.statusCode as ShiftRequestStatus]) ||
        String(current.statusCode),
      statusCode: current.statusCode,
      reviewDate: current.reviewDate ? formatToDdMmYyyy(current.reviewDate) : '',
      reviewerName: current.reviewerName ?? '',
      approvalComment: current.approverComment ?? '',
    }));
  }, [translate]);

  const handleApprove = useCallback(async () => {
    if (comment.trim().length === 0 || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await shiftService.resolveShiftChangeRequest(displayRequest.id, {
        decision: SHIFT_REQUEST_DECISION.APPROVED,
        approverComment: comment.trim(),
      });

      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:shiftChangeAdmin.messages.approveError'));
        return;
      }

      const data = response.data;

      if (data.outcome === SHIFT_APPROVAL_OUTCOME.ALREADY_RESOLVED) {
        showWarning(data.message || translate('attendance:shiftChangeAdmin.messages.alreadyResolved'));
        if (data.currentState) {
          applyResolvedState(data.currentState);
        }
        onConflict?.();
        return;
      }

      showSuccess(data.message || translate('attendance:shiftChangeAdmin.messages.approveSuccess'));
      onApproved?.();
    } finally {
      setIsSubmitting(false);
    }
  }, [
    comment,
    isSubmitting,
    displayRequest.id,
    showApiError,
    showWarning,
    showSuccess,
    translate,
    applyResolvedState,
    onApproved,
    onConflict,
  ]);

  const handleReject = useCallback(async () => {
    if (comment.trim().length === 0 || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await shiftService.resolveShiftChangeRequest(displayRequest.id, {
        decision: SHIFT_REQUEST_DECISION.REJECTED,
        approverComment: comment.trim(),
      });

      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:shiftChangeAdmin.messages.rejectError'));
        return;
      }

      const data = response.data;

      if (data.outcome === SHIFT_APPROVAL_OUTCOME.ALREADY_RESOLVED) {
        showWarning(data.message || translate('attendance:shiftChangeAdmin.messages.alreadyResolved'));
        if (data.currentState) {
          applyResolvedState(data.currentState);
        }
        onConflict?.();
        return;
      }

      showSuccess(data.message || translate('attendance:shiftChangeAdmin.messages.rejectSuccess'));
      onRejected?.();
    } finally {
      setIsSubmitting(false);
    }
  }, [
    comment,
    isSubmitting,
    displayRequest.id,
    showApiError,
    showWarning,
    showSuccess,
    translate,
    applyResolvedState,
    onRejected,
    onConflict,
  ]);

  return {
    displayRequest,
    comment,
    setComment,
    canSubmit,
    isSubmitting,
    handleApprove,
    handleReject,
  };
}

export default useShiftChangeRequestResolution;
